import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Message } from './../Common/Message'
import { OfflineHeader } from './../Header/OfflineHeader'

import { LogErrors } from './../../utilities/LogErrors'
import { 
    ValidateAccessCode as BLValidateAccessCode, 
    CreateAccount as BLCreateAccount, 
    AddToGroup as BLAddToGroup,
    ValidateAccessCodeInstructor as BLValidateAccessCodeInstructor,
    CreateInstructorAccessCode as BLCreateInstructorAccessCode,
    AddToCenterUserRequests as BLAddToCenterUserRequests
} from './../../utilities/BL/Student/SignUp/SignUp'
import { Credits } from './../Common/Credits/Credits'
import { GoToTop } from './../Common/GoToTop'
import { useFloatMessage, MESSAGE_TYPE } from './../Providers/FloatMessage'


const SignUp = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    const [sectionToShow,setSectionToShow] = useState('validate')
    const [login,setLogin] = useState(null)
    const [isInstructor, setIsInstructor] = useState(false)
    
    const [isWorking,setIsWorking] = useState(null)
    const [errorMessage,setErrorMessage] = useState(null)
    
    const [accessCode,setAccessCode] = useState(null)
    const [accountData,setAccountData] = useState(null)

    const [formValues, setFormValues] = useState({
        username: '',
        password: '',
        //confirmPassword: '',
        name: '',
        lastName: ''
    })

    const [showPassword, setShowPassword] = useState(false)
    //const [showConfirmPassword, setShowConfirmPassword] = useState(false)


    //--- Calls to API ---//

    const ValidateAccessCode = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            
            const isInstructorCode = accessCode && accessCode.length === 7 && accessCode.charAt(0).toUpperCase() === 'N';
            
            let response;
            if (isInstructorCode) {
                response = await BLValidateAccessCodeInstructor(accessCode)
            } else {
                response = await BLValidateAccessCode(accessCode)
            }
            
            if (response.success) {
                setIsInstructor(isInstructorCode)
                setSectionToShow('account')
            } else {
                setErrorMessage(response.message)
            }
        } catch (error) {
            await LogErrors('ValidateAccessCode - SignUp', error)
        } finally {
            setIsWorking(false)
        }
    }

    const CreateAccount = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            
            let response;
            if (isInstructor) {
                response = await BLCreateInstructorAccessCode(accountData)
            } else {
                response = await BLCreateAccount(accountData)
            }
            
            if (response.success) {
                setSectionToShow('ready')
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isInstructor
                        ? `¡Felicidades! Se ha creado tu cuenta de manera exitosa y se ha enviado tu solicitud al centro.`
                        : `¡Felicidades! Se ha creado y asociado tu cuenta de manera exitosa al grupo.`
                })
            } else {
                setErrorMessage(response.message)
            }
        } catch (error) {
            await LogErrors('CreateAccount - SignUp', error)
        } finally {
            setIsWorking(false)
        }
    }

    const Login = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            
            let response;
            if (isInstructor) {
                response = await BLAddToCenterUserRequests(accountData)
            } else {
                response = await BLAddToGroup(accountData)
            }
            
            if (response.success) {
                setSectionToShow('ready')
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isInstructor
                        ? `¡Felicidades! Se ha enviado tu solicitud al centro de manera exitosa.`
                        : `¡Felicidades! Se ha asociado tu cuenta al grupo de manera exitosa.`
                })
            } else {
                setErrorMessage(response.message)
            }
        } catch (error) {
            await LogErrors('Login - SignUp', error)
        } finally {
            setIsWorking(false)
        }
    }

    //--- Calls to API ---//


    //--- Eventos de formulario ---//
    const toggleShowPassword = () => setShowPassword(prev => !prev)
    //const toggleShowConfirmPassword = () => setShowConfirmPassword(prev => !prev)

    const handleInputChange = async (field, value) => {
        try {
            setFormValues(prev => ({ ...prev, [field]: value }))
            setAccountData(prev => ({ ...prev, [field]: value, accessCode })) // incluye accessCode    
        } catch (error) {
            await LogErrors('handleInputChange - Login', error)
        }
    }

    /**
     * Función que se ejecuta al presiona la tecla Enter en el formulario
     * @param {Event} e 
     */
    const EnterKeyPressInForm = async (e) => {
        try {
            if (e.key === 'Enter') {
                if (login) {
                    await Login()
                } else {
                    await CreateAccount()
                }
            }
        } catch (error) {
            await LogErrors('EnterKeyPressInForm - Login', error)
        }
    }
    //--- Eventos de formulario ---//

    const ShowLogin = async (response) => {
        try {
            setErrorMessage(null)
            setLogin(response)

            // Reiniciar los datos del formulario
            setFormValues({
                username: '',
                password: '',
                //confirmPassword: '',
                name: '',
                lastName: ''
            })

            // Reiniciar los datos enviados a la API
            setAccountData(null)
        } catch (error) {
            await LogErrors('ShowLogin - SignUp', error)
        }
    }

    useEffect(() => {
        //document.body.classList.add('DarkTheme')
        document.body.classList.add('BodyComplete')
        document.body.classList.add('Background--Ztudia')
        
        return () => {
            document.body.classList.remove('Background--Ztudia')
        }
    }, [])


    return (
        <>
            <OfflineHeader />

            <main className="SignUp container Container-FullHeight Fonts--PlusJakartaSans">
                {sectionToShow && sectionToShow === 'validate' && (
                    <section>
                        <p className="SignUp__Title">Bienvenid@ a</p>
                        <img src="/img/brand/Ztudia-Logotipo-Negro-350x135.png" className="SignUp__Image" />
                        <p className="SignUp__Text">Como primer paso, valida tu código de Ztudia</p>

                        <div className="SignUp__Form">
                            <input type='text' placeholder='Escribe tu código de Ztudia'  className="SignUp__Form__Input" value={accessCode || ''} onChange={(e) => setAccessCode(e.target.value)} />

                            {isWorking ? (
                                <button type='button' className="Btn__Ztudia--Disabled SignUp__Form__Button">Trabajando...</button>
                            ) : (
                                <button type='button' className="Btn__Ztudia--Primary SignUp__Form__Button" onClick={ValidateAccessCode}>Validar código de Ztudia</button>
                            )}
                            {errorMessage && errorMessage !== '' && ( <p className='SignUp__Form__Error'>{errorMessage}</p> )}
                        </div>

                        <p className="SignUp__Text">¿No cuentas con un código de Ztudia?</p>
                        <p className="SignUp__Text">Consulta con tu institución</p>
                    </section>
                )}
                
                {sectionToShow && sectionToShow === 'account' && (
                    <section>
                        <Message icon={'bi bi-check-circle-fill'} title={'¡Muy bien!'} subtitle={`Tu código de Ztudia es válido${accessCode && " (" + accessCode + ")"}`}/>

                        {login ? (
                            <div>
                                {/* Iniciar sesión */}
                                <p className='SignUp__Text SignUp__Text--SpaceUp'>Para finalizar el proceso de asociarte al {isInstructor ? 'centro' : 'nuevo grupo'}, ingresa los datos de tu cuenta Ztudia</p>

                                <div className='SignUp__Form'>
                                    <p className="SignUp__Title">Iniciar sesión</p>
                                    
                                    <input type='text' className='SignUp__Form__Input' placeholder='Usuario' value={formValues.username} onChange={(e) => handleInputChange('username', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                    
                                    <div className="SignUp__Form__InputContainer">
                                        <input type={showPassword ? 'text' : 'password'} className='SignUp__Form__InputContainer__Input' placeholder='Contraseña' value={formValues.password} onChange={(e) => handleInputChange('password', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                        <i className={`SignUp__Form__InputContainer__Icon bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} onClick={toggleShowPassword}></i>
                                    </div>

                                    {isWorking ? (
                                        <button type='button' className="Btn__Ztudia--Disabled SignUp__Form__Button">Trabajando...</button>
                                    ) : (
                                        <button type='button' className='Btn__Ztudia--Primary SignUp__Form__Button' onClick={Login}>{isInstructor ? 'Asociarme al centro ahora' : 'Asociarme al grupo ahora'}</button>
                                    )}
                                    {errorMessage && errorMessage !== '' && ( <p className='SignUp__Form__Error'>{errorMessage}</p> )}
                                </div>

                                <button type='button' className='Btn__Ztudia--Disabled SignUp__Button--Center' onClick={() => ShowLogin(false)}>¿Aún no tienes cuenta? Crea una aquí</button>
                            </div>
                        ) : (
                            <div>
                                {/* Crear cuenta */}
                                <p className='SignUp__Text SignUp__Text--SpaceUp'>Para asociarte al {isInstructor ? 'centro' : 'grupo'}, inicia sesión o crea tu cuenta Ztudia</p>

                                <button type='button' className='Btn__Ztudia--Primary SignUp__Button--Center' onClick={() => ShowLogin(true)}>Iniciar sesión aquí</button>

                                <p className='SignUp__Text SignUp__Text--SpaceUp'>O</p>

                                <div className='SignUp__Form'>
                                    <p className="SignUp__Title">Crear mi cuenta Ztudia</p>

                                    <input type='text' className='SignUp__Form__Input' placeholder='Usuario' value={formValues.username} onChange={(e) => handleInputChange('username', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                    <p className='SignUp__Text--Small SignUp__Text--BottomZero'>Solo letras y números</p>
                                    
                                    {/* <div className="SignUp__Form__InputContainer">
                                        <input type={showPassword ? 'text' : 'password'} className='SignUp__Form__InputContainer__Input' placeholder='Contraseña' value={formValues.password} onChange={(e) => handleInputChange('password', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                        <i className={`SignUp__Form__InputContainer__Icon bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} onClick={toggleShowPassword}></i>
                                    </div>

                                    <div className="SignUp__Form__InputContainer">
                                        <input type={showConfirmPassword ? 'text' : 'password'} className='SignUp__Form__InputContainer__Input' placeholder='Repetir contraseña' value={formValues.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                        <i className={`SignUp__Form__InputContainer__Icon bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`} onClick={toggleShowConfirmPassword}></i>
                                    </div> */}

                                    <input type='text' className='SignUp__Form__Input' placeholder='Nombre(s)' value={formValues.name} onChange={(e) => handleInputChange('name', e.target.value)} onKeyDown={EnterKeyPressInForm}/>
                                    
                                    <input type='text' className='SignUp__Form__Input' placeholder='Apellido(s)' value={formValues.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} onKeyDown={EnterKeyPressInForm}/>

                                    {isWorking ? (
                                        <button type='button' className="Btn__Ztudia--Disabled SignUp__Form__Button">Trabajando...</button>
                                    ) : (
                                        <button type='button' className='Btn__Ztudia--Primary SignUp__Form__Button' onClick={CreateAccount}>Crear mi cuenta ahora</button>
                                    )}
                                    {errorMessage && errorMessage !== '' && ( <p className='SignUp__Form__Error'>{errorMessage}</p> )}
                                </div>
                            </div>
                        )}
                    </section>
                )}
                
                {sectionToShow && sectionToShow === 'ready' && (
                    <section>
                        <Message 
                            icon={'bi bi-check-circle-fill'} 
                            title={'¡Bien hecho!'} 
                            subtitle={
                                login ? (
                                    isInstructor 
                                        ? 'Se ha enviado tu solicitud al centro de manera exitosa' 
                                        : 'Te has asociado al grupo de manera exitosa'
                                ) : (
                                    isInstructor 
                                        ? 'Se ha creado tu cuenta Ztudia y se ha enviado tu solicitud al centro de manera exitosa' 
                                        : 'Se ha creado tu cuenta Ztudia y te has asociado al grupo de manera exitosa'
                                )
                            } 
                        />
                        
                        <p className='SignUp__Text SignUp__Text--SpaceUp'>Ya estás list@ para comenzar tu proceso</p>

                        <button type='button' className='Btn__Ztudia--Primary SignUp__Button--Center' onClick={() => navigate('/dashboard')}>Ir a iniciar sesión</button>
                    </section>
                )}
            </main>

            <Credits />

            <GoToTop />
        </>
    )
}

export { SignUp };